import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import * as PublishedEditionsAPI from '../../api/PublishedEditions';
import { ReaderData } from '../../api/PublishedEditions';
import CoverPage from './CoverPage';
import StoryPage from './StoryPage';
import PasswordGate from './PasswordGate';

export default function NewspaperReader() {
  const { slug } = useParams<{ slug: string }>();

  const [data, setData] = useState<ReaderData | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordMeta, setPasswordMeta] = useState<{ newspaper_name: string; edition_name: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    PublishedEditionsAPI.read(slug)
      .then((res) => {
        const d = res.data as any;
        if (d.requires_password) {
          setNeedsPassword(true);
          setPasswordMeta({ newspaper_name: d.newspaper_name, edition_name: d.edition_name });
        } else {
          setData(d.data ?? d);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          const d = e.response.data;
          if (d?.requires_password) {
            setNeedsPassword(true);
            setPasswordMeta({ newspaper_name: d.newspaper_name, edition_name: d.edition_name });
          } else {
            setError('This newspaper requires you to log in.');
          }
        } else {
          setError('Newspaper not found.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [slug]);

  const handleVerifyPassword = useCallback(async (password: string): Promise<boolean> => {
    if (!slug) return false;
    try {
      const res = await PublishedEditionsAPI.verifyPassword(slug, password);
      const d = (res.data as any).data ?? res.data;
      setData(d);
      setNeedsPassword(false);
      return true;
    } catch {
      return false;
    }
  }, [slug]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    window.scrollTo(0, 0);
  }, []);

  // Handle swipe gestures
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!data) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const totalSlides = data.stories.length + 1; // +1 for cover

    // Only swipe if horizontal movement > vertical and > threshold
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0 && currentIndex < totalSlides - 1) {
        goToSlide(currentIndex + 1);
      } else if (dx > 0 && currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!data) return;
      const totalSlides = data.stories.length + 1;
      if (e.key === 'ArrowRight' && currentIndex < totalSlides - 1) {
        goToSlide(currentIndex + 1);
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data, currentIndex, goToSlide]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#faf7f0' }}>
        <CircularProgress sx={{ color: '#163567' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#faf7f0', px: 3 }}>
        <Typography variant="h6" color="text.secondary" sx={{ fontFamily: '"Georgia", serif' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (needsPassword && passwordMeta) {
    return (
      <PasswordGate
        newspaperName={passwordMeta.newspaper_name}
        editionName={passwordMeta.edition_name}
        onVerify={handleVerifyPassword}
      />
    );
  }

  if (!data) return null;

  const totalSlides = data.stories.length + 1;

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        minHeight: '100vh',
        background: '#fff',
        position: 'relative',
        overflowX: 'hidden',
      }}
    >
      {/* Progress dots */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        display: 'flex',
        gap: '3px',
        px: 1,
        py: 0.5,
        background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(8px)',
      }}>
        {Array.from({ length: totalSlides }).map((_, i) => (
          <Box
            key={i}
            onClick={() => goToSlide(i)}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: i === currentIndex ? '#163567' : '#ddd',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          />
        ))}
      </Box>

      {/* Content */}
      <Box sx={{ pt: '12px' }}>
        {currentIndex === 0 ? (
          <CoverPage data={data} onStoryClick={(i) => goToSlide(i)} />
        ) : (
          <StoryPage
            story={data.stories[currentIndex - 1]}
            index={currentIndex - 1}
            total={data.stories.length}
            newspaperName={data.newspaper_name}
          />
        )}
      </Box>

      {/* Left/right tap zones for desktop */}
      <Box
        onClick={() => { if (currentIndex > 0) goToSlide(currentIndex - 1); }}
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: '15%',
          cursor: currentIndex > 0 ? 'pointer' : 'default',
          zIndex: 5,
        }}
      />
      <Box
        onClick={() => { if (currentIndex < totalSlides - 1) goToSlide(currentIndex + 1); }}
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '15%',
          cursor: currentIndex < totalSlides - 1 ? 'pointer' : 'default',
          zIndex: 5,
        }}
      />
    </Box>
  );
}
